/**
 * Appendix A state machines. Each machine lists its canonical states and the allowed forward transitions;
 * everything else is forbidden. The explicitly forbidden transitions from Appendix A are also listed as data
 * (`APPENDIX_A_FORBIDDEN`) so tests can assert each one by name.
 */
import { ClaimStatus, DrawStatus, EntryStatus, FulfillmentStatus, HoldStatus, LotteryStatus, PaymentStatus, SupportCaseStatus, WinnerContactStatus, } from './enums.js';
export class ForbiddenTransitionError extends Error {
    machine;
    from;
    to;
    reason;
    code = 'forbidden_transition';
    constructor(machine, from, to, reason) {
        super(`${machine}: transition ${from} → ${to} is not allowed${reason ? ` (${reason})` : ''}`);
        this.machine = machine;
        this.from = from;
        this.to = to;
        this.reason = reason;
        this.name = 'ForbiddenTransitionError';
    }
}
export const defineMachine = (def) => {
    const stateSet = new Set(def.states);
    for (const [from, targets] of Object.entries(def.transitions)) {
        if (!stateSet.has(from))
            throw new Error(`${def.name}: unknown source state ${from}`);
        for (const t of targets)
            if (!stateSet.has(t))
                throw new Error(`${def.name}: unknown target state ${t}`);
    }
    const canTransition = (from, to) => from !== to && (def.transitions[from] ?? []).includes(to);
    return {
        ...def,
        canTransition,
        assertTransition(from, to) {
            if (!canTransition(from, to))
                throw new ForbiddenTransitionError(def.name, from, to);
        },
        nextStates: (from) => def.transitions[from] ?? [],
        isTerminal: (state) => (def.transitions[state] ?? []).length === 0,
        terminalStates: () => def.states.filter((s) => (def.transitions[s] ?? []).length === 0),
        isState: (value) => typeof value === 'string' && stateSet.has(value),
    };
};
/** §5.4 Draft → Review → Approved → Scheduled → Live → Closed → Drawn → Settled; Paused / Cancelled branches. */
export const LotteryMachine = defineMachine({
    name: 'Lottery',
    states: LotteryStatus.options,
    transitions: {
        DRAFT: ['REVIEW', 'CANCELLED'],
        REVIEW: ['APPROVED', 'DRAFT', 'CANCELLED'],
        APPROVED: ['SCHEDULED', 'DRAFT', 'CANCELLED'],
        SCHEDULED: ['LIVE', 'PAUSED', 'CANCELLED'],
        LIVE: ['PAUSED', 'CLOSED', 'CANCELLED'],
        PAUSED: ['LIVE', 'CLOSED', 'CANCELLED'],
        CLOSED: ['DRAWN', 'CANCELLED'],
        DRAWN: ['SETTLED'],
        SETTLED: [],
        CANCELLED: [],
    },
});
/** §10.2 Initialized → Pending → Succeeded / Failed / Expired / Cancelled; Refunded and Reconciliation Exception linked. */
export const PaymentMachine = defineMachine({
    name: 'Payment',
    states: PaymentStatus.options,
    transitions: {
        INITIALIZED: ['PENDING', 'SUCCEEDED', 'FAILED', 'EXPIRED', 'CANCELLED'],
        PENDING: ['SUCCEEDED', 'FAILED', 'EXPIRED', 'CANCELLED', 'RECONCILIATION_EXCEPTION'],
        SUCCEEDED: ['PARTIALLY_REFUNDED', 'REFUNDED', 'RECONCILIATION_EXCEPTION'],
        PARTIALLY_REFUNDED: ['REFUNDED', 'RECONCILIATION_EXCEPTION'],
        RECONCILIATION_EXCEPTION: ['SUCCEEDED', 'FAILED', 'REFUNDED'],
        // Terminal: out-of-order events cannot move a terminal payment backward (§10.3 #4).
        FAILED: [],
        EXPIRED: [],
        CANCELLED: [],
        REFUNDED: [],
    },
});
/** Entry: Pending → Eligible → Snapshot Included; Ineligible / Refunded / Disqualified with reasons. */
export const EntryMachine = defineMachine({
    name: 'Entry',
    states: EntryStatus.options,
    transitions: {
        PENDING: ['ELIGIBLE', 'INELIGIBLE', 'REFUNDED'],
        ELIGIBLE: ['SNAPSHOT_INCLUDED', 'INELIGIBLE', 'REFUNDED', 'DISQUALIFIED'],
        SNAPSHOT_INCLUDED: ['DISQUALIFIED'],
        INELIGIBLE: ['REFUNDED'],
        REFUNDED: [],
        DISQUALIFIED: [],
    },
});
/** Draw: Committed → Snapshot Locked → Allocated → Published → Revealed → Verified; Failed / Cancelled sealed. */
export const DrawMachine = defineMachine({
    name: 'Draw',
    states: DrawStatus.options,
    transitions: {
        COMMITTED: ['SNAPSHOT_LOCKED', 'FAILED', 'CANCELLED'],
        SNAPSHOT_LOCKED: ['ALLOCATED', 'FAILED', 'CANCELLED'],
        ALLOCATED: ['PUBLISHED', 'FAILED', 'CANCELLED'],
        PUBLISHED: ['REVEALED'],
        REVEALED: ['VERIFIED'],
        VERIFIED: [],
        FAILED: [],
        CANCELLED: [],
    },
});
/** Variant hold: Active → Converted / Expired / Released; one active hold per win. */
export const VariantHoldMachine = defineMachine({
    name: 'VariantHold',
    states: HoldStatus.options,
    transitions: {
        ACTIVE: ['CONVERTED', 'EXPIRED', 'RELEASED'],
        CONVERTED: [],
        EXPIRED: [],
        RELEASED: [],
    },
});
/** §11.1 claim lifecycle with Expired / Forfeited / Redrawn as terminal branches. */
export const ClaimMachine = defineMachine({
    name: 'Claim',
    states: ClaimStatus.options,
    transitions: {
        OPENED: ['VARIANT_HELD', 'SUBMITTED', 'EXPIRED', 'FORFEITED'],
        VARIANT_HELD: ['SUBMITTED', 'OPENED', 'EXPIRED', 'FORFEITED'],
        SUBMITTED: ['VERIFICATION_PENDING', 'EXPIRED', 'FORFEITED'],
        VERIFICATION_PENDING: ['VERIFIED', 'MORE_INFO', 'REJECTED', 'EXPIRED', 'FORFEITED'],
        VERIFIED: ['APPROVED', 'REJECTED', 'FORFEITED'],
        MORE_INFO: ['SUBMITTED', 'VERIFICATION_PENDING', 'EXPIRED', 'FORFEITED'],
        // An appeal re-enters review; the redraw policy applies only after the appeal window (§11.2).
        REJECTED: ['VERIFICATION_PENDING', 'REDRAWN', 'FORFEITED'],
        APPROVED: ['IN_FULFILLMENT', 'FORFEITED'],
        IN_FULFILLMENT: ['PAID', 'DELIVERED', 'FORFEITED'],
        PAID: [],
        DELIVERED: [],
        EXPIRED: ['REDRAWN', 'FORFEITED'],
        FORFEITED: [],
        REDRAWN: [],
    },
});
/** §12.3 Not yet messaged → Button tapped → Messaged → Followed up. */
export const WinnerContactMachine = defineMachine({
    name: 'WinnerContact',
    states: WinnerContactStatus.options,
    transitions: {
        NOT_MESSAGED: ['BUTTON_TAPPED', 'MESSAGED'],
        BUTTON_TAPPED: ['MESSAGED'],
        MESSAGED: ['FOLLOWED_UP'],
        FOLLOWED_UP: [],
    },
});
/** Fulfillment: Not Ready → Ready → In Progress → Exception → Completed. */
export const FulfillmentMachine = defineMachine({
    name: 'Fulfillment',
    states: FulfillmentStatus.options,
    transitions: {
        NOT_READY: ['READY'],
        READY: ['IN_PROGRESS', 'EXCEPTION'],
        IN_PROGRESS: ['EXCEPTION', 'COMPLETED'],
        EXCEPTION: ['IN_PROGRESS', 'COMPLETED'],
        COMPLETED: [],
    },
});
/** Support case: Open → Assigned → Waiting on Participant / Platform → Resolved → Closed. */
export const SupportCaseMachine = defineMachine({
    name: 'SupportCase',
    states: SupportCaseStatus.options,
    transitions: {
        OPEN: ['ASSIGNED', 'RESOLVED', 'CLOSED'],
        ASSIGNED: ['WAITING_PARTICIPANT', 'WAITING_PLATFORM', 'RESOLVED'],
        WAITING_PARTICIPANT: ['ASSIGNED', 'WAITING_PLATFORM', 'RESOLVED', 'CLOSED'],
        WAITING_PLATFORM: ['ASSIGNED', 'WAITING_PARTICIPANT', 'RESOLVED'],
        RESOLVED: ['CLOSED', 'ASSIGNED'],
        CLOSED: [],
    },
});
export const MACHINES = {
    Lottery: LotteryMachine,
    Payment: PaymentMachine,
    Entry: EntryMachine,
    Draw: DrawMachine,
    VariantHold: VariantHoldMachine,
    Claim: ClaimMachine,
    WinnerContact: WinnerContactMachine,
    Fulfillment: FulfillmentMachine,
    SupportCase: SupportCaseMachine,
};
/** "Pending payment → eligible entry without authenticated provider evidence" is forbidden. */
export const canConfirmPaidEntry = (payment) => payment.status === 'SUCCEEDED' && payment.providerEvidenceVerified;
export const assertPaidEntryConfirmable = (payment) => {
    if (!canConfirmPaidEntry(payment)) {
        throw new ForbiddenTransitionError('Entry', 'PENDING', 'ELIGIBLE', 'paid entries need authenticated provider evidence of a succeeded payment');
    }
};
/** "Button tapped → marked messaged without an admin action" is forbidden: only STAFF may mark MESSAGED / FOLLOWED_UP. */
export const canTransitionWinnerContact = (from, to, actor) => {
    if (!WinnerContactMachine.canTransition(from, to))
        return false;
    if ((to === 'MESSAGED' || to === 'FOLLOWED_UP') && actor !== 'STAFF')
        return false;
    return true;
};
export const assertWinnerContactTransition = (from, to, actor) => {
    if (!canTransitionWinnerContact(from, to, actor)) {
        throw new ForbiddenTransitionError('WinnerContact', from, to, `actor ${actor} may not perform this change`);
    }
};
/** "Published outcome → edited winner list without a new redraw and evidence package": a published draw is immutable. */
export const DRAW_IMMUTABLE_FROM = ['PUBLISHED', 'REVEALED', 'VERIFIED'];
export const isDrawImmutable = (status) => DRAW_IMMUTABLE_FROM.includes(status);
/** "Rejected or expired claim → delivered prize without a recorded reversal decision and approval." */
export const CLAIM_STATES_WITHOUT_DELIVERY_RIGHTS = [
    'REJECTED',
    'EXPIRED',
    'FORFEITED',
    'REDRAWN',
];
/** Appendix A "Forbidden transitions", as data: each has a machine edge or guard that encodes it. */
export const APPENDIX_A_FORBIDDEN = [
    {
        text: 'Pending payment → eligible entry without authenticated provider evidence',
        guard: 'assertPaidEntryConfirmable',
    },
    {
        text: 'Published outcome → edited winner list without a new redraw and evidence package',
        machine: 'Draw',
        from: 'PUBLISHED',
        to: 'ALLOCATED',
    },
    {
        text: 'Rejected or expired claim → delivered prize without a recorded reversal decision and approval',
        machine: 'Claim',
        from: 'REJECTED',
        to: 'DELIVERED',
    },
    {
        text: 'Rejected or expired claim → delivered prize (expired branch)',
        machine: 'Claim',
        from: 'EXPIRED',
        to: 'DELIVERED',
    },
    {
        text: 'Refunded payment → erased payment, entry or win history',
        machine: 'Payment',
        from: 'REFUNDED',
        to: 'INITIALIZED',
    },
    {
        text: 'Button tapped → marked messaged without an admin action',
        guard: 'assertWinnerContactTransition',
    },
    {
        text: 'Withdrawn marketing consent → continued campaign export under the same purpose',
        guard: 'consent (analytics module: exports filter on current consent state)',
    },
    {
        text: 'Cancelled lottery → deleted fairness or financial artifacts',
        machine: 'Lottery',
        from: 'CANCELLED',
        to: 'DRAFT',
    },
];
//# sourceMappingURL=state-machines.js.map